import debounce from "lodash/debounce";
import React from "react";
import { VisuallyHidden, useObjectRef } from "react-aria";
import { ButtonContext } from "react-aria-components";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../Clickables";
import { Svg } from "../Svg";

import styles from "./Carousel.module.scss";
import dotStyles from "./Dots.module.scss";

export type CarouselProps = React.PropsWithChildren<{
    label: string;
    loop?: boolean;
    advanceBy?: "page" | "item";
    controls?:
        | null
        | React.ReactNode
        | ((props: { allVisible: boolean }) => React.ReactNode);
    dots?:
        | null
        | React.ReactNode
        | ((props: {
              allVisible: boolean;
              slideCount: number;
          }) => React.ReactNode);
    onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
    containerProps?: React.ComponentPropsWithRef<"div">;
    scrollContainerProps?: React.ComponentPropsWithRef<"div">;
    onActiveChange?: (activeIndexes: Set<number>) => void;
}>;

function useDebouncedValue<T>(value: T, delay: number) {
    const [debouncedValue, setDebouncedValue] = React.useState(value);
    const debouncedSetValue = React.useCallback(
        debounce(setDebouncedValue, delay),
        [delay],
    );
    React.useEffect(() => {
        debouncedSetValue(value);
    }, [value, debouncedSetValue]);
    return debouncedValue;
}

function LiveRegion({ contents }: { contents: string }) {
    const debouncedContents = useDebouncedValue(contents, 100);
    return (
        <VisuallyHidden aria-live="polite" aria-atomic="true">
            {debouncedContents}
        </VisuallyHidden>
    );
}

/* eslint-disable */
function formatIndexes(indexes: Set<number>): string {
    const listFormat =
        // @ts-expect-error: Not aware of `ListFormat`
        typeof Intl.ListFormat !== "undefined"
            ? // @ts-expect-error: 🙄
              new Intl.ListFormat("en")
            : null;
    return listFormat
        ? listFormat.format(Array.from(indexes).map((i) => i.toString()))
        : Array.from(indexes).join(", ");
}
/* eslint-enable */

function eqSet(a: Set<unknown>, b: Set<unknown>) {
    if (a.size !== b.size) return false;
    for (const item of a) {
        if (!b.has(item)) return false;
    }
    return true;
}

/**
 * This is a hook that tracks the active slides in a carousel. It uses an
 * intersection observer to determine which slides are visible to the user.
 *
 * It expects the following attributes to be set on the slides:
 * - data-slide-index - The 0-indexed index of the slide
 * - aria-roledescription="slide"
 *
 * @param container - The container element that contains the slides.
 * @param threshold - The threshold for determining if a slide is visible.
 */
function useActiveSlides(
    container: React.RefObject<HTMLElement | null>,
    threshold: number,
) {
    const [state, setState] = React.useState(new Set<number>());

    React.useEffect(() => {
        if (!container.current) return;
        const elementsToWatch = container.current.querySelectorAll(
            "[aria-roledescription='slide']",
        );

        elementsToWatch.forEach((element) => {
            if (getComputedStyle(element).display === "contents") {
                console.error(
                    "useActiveSlides's IntersectionObserver doesn't see this element because `display: contents` is applied to it, and therefore renders it as `inert`.",
                    element,
                );
            }
        });

        const observer = new IntersectionObserver(
            (entries) => {
                setState((state) => {
                    const visibleEntryIds = new Set<number>(state);
                    for (const entry of entries) {
                        const key = Number(
                            (entry.target as HTMLElement).dataset.slideIndex,
                        );
                        if (entry.intersectionRatio > threshold) {
                            visibleEntryIds.add(key);
                        } else {
                            visibleEntryIds.delete(key);
                        }
                    }
                    // If the state has not changed return the original set so nothing is re-rendered
                    return eqSet(state, visibleEntryIds)
                        ? state
                        : visibleEntryIds;
                });
            },
            { threshold, root: container.current },
        );
        elementsToWatch.forEach((element) => observer.observe(element));

        return () => {
            observer.disconnect();
        };
    }, []);

    return state;
}

function useAllVisible(container: React.RefObject<HTMLElement | null>) {
    const [allVisible, setAllVisible] = React.useState(true);
    React.useEffect(() => {
        function onResize() {
            setAllVisible(
                container.current?.scrollWidth ===
                    container.current?.clientWidth,
            );
        }
        onResize();
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);
    return allVisible;
}

/**
 * A generic carousel component. Note: avoid using display:contents on slides - it breaks IntersectionObserver, which this depends on.
 */
export function Carousel({
    children,
    label,
    loop = true,
    advanceBy = "page",
    controls,
    dots,
    onScroll,
    containerProps,
    scrollContainerProps,
    onActiveChange,
}: CarouselProps) {
    const container = useObjectRef(containerProps?.ref);
    const scrollContainer = useObjectRef(scrollContainerProps?.ref);
    const id = React.useId();
    const labelId = `${id}-label`;

    if (!label || label.includes("carousel"))
        console.warn(
            "The carousel label should not include the word 'carousel':\n\
                https://www.w3.org/WAI/ARIA/apg/patterns/carousel/#:~:text=Note%20that%20since%20the%20aria%2Droledescription%20is%20set%20to%20%22carousel%22%2C%20the%20label%20does%20not%20contain%20the%20word%20%22carousel%22.",
        );

    /**
     * The ratio of visibility that triggers the intersection observer.
     * This sets it in one spot and reads it in another, so this will keep
     * them linked.
     *
     * The value is somewhat arbitrary, and honestly it might need to be
     * configurable down the line. It was originally `1`, but that had problems
     * when the ratio was something like 0.9999956, and if the slide is a link,
     * a user might want to click that's 80% visible but would be unable to.
     */
    const activeIndexes = useActiveSlides(container, 0.6);
    React.useEffect(() => {
        onActiveChange?.(activeIndexes);
    }, [activeIndexes, onActiveChange]);

    // If the first slide is active, we can't go back any further
    // so if we're looping, we can go to the last slide, otherwise we're disabled
    const prevScrollBehavior = activeIndexes.has(0)
        ? loop
            ? "last"
            : "disabled"
        : "adjacent";

    function goToPrevious() {
        const scroller = scrollContainer.current;
        if (!scroller) return;
        if (prevScrollBehavior === "last") {
            scroller.scrollTo({
                left: scroller.scrollWidth,
                behavior: "smooth",
            });
        } else {
            if (advanceBy === "page") {
                scroller.scrollBy({
                    left: -scroller.clientWidth,
                    behavior: "smooth",
                });
            } else {
                const slides = container.current?.querySelectorAll(
                    "[aria-roledescription='slide']",
                );
                if (!slides) return;
                // Find the slide immediately to the left of the first active slide
                for (const slide of slides) {
                    if (!(slide instanceof HTMLElement)) continue;

                    // Check if the slide is visible - since we're checking from left to right
                    // that would mean that the previous slide is the one to scroll to
                    if (activeIndexes.has(Number(slide.dataset.slideIndex))) {
                        const prev = slide.previousElementSibling;
                        if (!(prev instanceof HTMLElement)) continue;
                        scroller.scrollTo({
                            left: prev.offsetLeft,
                            behavior: "smooth",
                        });
                        break;
                    }
                }
            }
        }
    }
    const slideCount = React.Children.count(children);

    // If the last slide is active, we can't go forward any further
    // so if we're looping, we can go to the first slide, otherwise we're disabled
    const nextScrollBehavior = activeIndexes.has(slideCount - 1)
        ? loop
            ? "first"
            : "disabled"
        : "adjacent";

    function goToNext() {
        const scroller = scrollContainer.current;
        if (!scroller) return;
        if (nextScrollBehavior === "first") {
            scroller.scrollTo({ left: 0, behavior: "smooth" });
        } else {
            if (advanceBy === "page") {
                scroller.scrollBy({
                    left: scroller.clientWidth,
                    behavior: "smooth",
                });
            } else {
                const slides = container.current?.querySelectorAll(
                    "[aria-roledescription='slide']",
                );
                if (!slides) return;
                // From the left, find the first slide that is not active
                for (const slide of slides) {
                    const prev = slide.previousElementSibling;
                    if (
                        !(slide instanceof HTMLElement) ||
                        !(prev instanceof HTMLElement)
                    )
                        continue;

                    // Check if the previous slide is visible - since we're checking from left to right
                    // that would mean this slide is the one to scroll to
                    if (activeIndexes.has(Number(prev.dataset.slideIndex))) {
                        scroller.scrollTo({
                            left: slide.offsetLeft,
                            behavior: "smooth",
                        });
                        break;
                    }
                }
            }
        }
    }

    const allVisible = useAllVisible(scrollContainer);

    if (typeof controls === "function") {
        controls = controls({ allVisible });
    }

    if (typeof dots === "function") {
        dots = dots({ allVisible, slideCount });
    }

    return (
        <div
            {...containerProps}
            role="group" // might need an option to make this a region
            aria-roledescription="carousel"
            ref={container}
        >
            <VisuallyHidden id={labelId}>{label}</VisuallyHidden>
            <LiveRegion contents={formatIndexes(activeIndexes)} />
            <ButtonContext
                value={{
                    slots: {
                        previous: {
                            onPress: goToPrevious,
                            ["aria-label"]:
                                prevScrollBehavior === "last"
                                    ? t`Go to last item`
                                    : t`Previous item`,
                            isDisabled: prevScrollBehavior === "disabled",
                        },
                        next: {
                            onPress: goToNext,
                            ["aria-label"]:
                                nextScrollBehavior === "first"
                                    ? t`Go to first item`
                                    : t`Next item`,
                            isDisabled: nextScrollBehavior === "disabled",
                        },
                    },
                }}
            >
                {controls === null || controls === false ? null : controls ===
                      true || typeof controls === "undefined" ? (
                    <div
                        className={styles.controls}
                        style={{ display: allVisible ? "none" : "block" }}
                    >
                        <Clickable
                            slot="previous"
                            className={concatClassNames([
                                styles.navArrow,
                                styles.previous,
                            ])}
                        >
                            <Svg name="arrow" />
                        </Clickable>
                        <Clickable slot="next" className={styles.navArrow}>
                            <Svg name="arrow" />
                        </Clickable>
                    </div>
                ) : (
                    controls
                )}
            </ButtonContext>
            <div
                {...scrollContainerProps}
                className={concatClassNames([
                    styles.scrollContainer,
                    scrollContainerProps?.className,
                ])}
                ref={scrollContainer}
                onScroll={(e) => {
                    onScroll?.(e);
                    scrollContainerProps?.onScroll?.(e);
                }}
            >
                <div
                    aria-live="off"
                    aria-atomic="false"
                    className={styles.slidesWrapper}
                >
                    {React.Children.map(children, (child, index) => {
                        const slideId = `${id}-slide-${index}`;
                        return (
                            <div
                                role="group"
                                data-slide-index={index}
                                inert={!activeIndexes.has(index)}
                                aria-roledescription="slide"
                                aria-labelledby={slideId}
                                className={styles.slide}
                            >
                                {child}
                            </div>
                        );
                    })}
                </div>
            </div>

            <ButtonContext
                value={{
                    slots: Object.fromEntries(
                        React.Children.toArray(children).map((_, i) => {
                            const slideId = `${id}-slide-${i}`;
                            return [
                                `slide-${i}`,
                                {
                                    id: slideId,
                                    ["aria-disabled"]: activeIndexes.has(i),
                                    onPress: () => {
                                        const scroller =
                                            scrollContainer.current;
                                        if (activeIndexes.has(i) || !scroller)
                                            return;
                                        const slide = document.querySelector(
                                            `[aria-labelledby="${slideId}"]`,
                                        );
                                        if (!(slide instanceof HTMLElement))
                                            return;
                                        scroller.scrollTo({
                                            left: slide.offsetLeft,
                                            behavior: "smooth",
                                        });
                                    },
                                    children: (
                                        <VisuallyHidden>{`${i + 1} of ${slideCount}`}</VisuallyHidden>
                                    ),
                                    ["data-active"]: activeIndexes.has(i)
                                        ? true
                                        : undefined,
                                },
                            ] as const;
                        }),
                    ),
                }}
            >
                {dots === null || dots === false ? null : dots === true ||
                  typeof dots === "undefined" ? (
                    <div
                        className={dotStyles.root}
                        style={{ display: allVisible ? "none" : "block" }}
                        role="group"
                        aria-label={t`Choose slide to display`}
                    >
                        {React.Children.map(children, (_, i) => {
                            return (
                                <Clickable
                                    key={i}
                                    slot={`slide-${i}`}
                                    className={
                                        activeIndexes.has(i)
                                            ? dotStyles.dotInactive
                                            : dotStyles.dotActive
                                    }
                                />
                            );
                        })}
                    </div>
                ) : React.isValidElement(dots) ? (
                    React.cloneElement(dots, {
                        role: "group",
                        ["aria-label"]: t`Choose slide to display`,
                    } as React.HTMLProps<HTMLElement>)
                ) : (
                    <div role="group" aria-label={t`Choose slide to display`}>
                        {dots}
                    </div>
                )}
            </ButtonContext>
        </div>
    );
}
